/**
 * Escrever telefone. Funções puras, sem React.
 *
 * Elas moram aqui, e não junto do [CampoTelefone](../../components/design-new/campo-telefone.tsx),
 * porque a **página do cliente** também precisa escrever telefone, e ela não usa
 * aquele campo (a página tem as cores e a forma do personal, não as do painel).
 * Importando de lá, a landing carregava o componente inteiro junto: o Radix, o
 * tema e a lista dos quarenta países entravam no primeiro carregamento da página
 * de vendas, 50KB de JavaScript para desenhar um parêntese.
 *
 * O campo do painel continua sendo o dono da regra por PAÍS (ele importa daqui).
 */

/** Máscara nacional do Brasil, onde 0 é dígito. */
export const MASCARA_BR = "(00) 00000-0000"

/** Só os dígitos. */
export function soDigitos(valor: string): string {
  return (valor || "").replace(/\D/g, "")
}

/**
 * Aplica uma máscara sobre os dígitos, parando onde eles acabam (é isso que faz
 * a escrita acompanhar a digitação em vez de mostrar separador solto no fim).
 *
 * Sem máscara, agrupa de três em três: é o que sobra para país que não tem
 * formato declarado.
 */
export function aplicarMascaraTelefone(digitos: string, mascara?: string): string {
  const d = soDigitos(digitos)
  if (!mascara) {
    return d.slice(0, 14).replace(/(\d{3})(?=\d)/g, "$1 ")
  }
  let saida = ""
  let i = 0
  for (const c of mascara) {
    if (i >= d.length) break
    if (c === "0") {
      saida += d[i]
      i += 1
    } else {
      saida += c
    }
  }
  return saida
}

/**
 * Os dígitos de um telefone brasileiro, do jeito que a API guarda: DDD mais o
 * número, no máximo 11.
 *
 * O teto existe porque nada limitava o tamanho e dava para digitar
 * `121212121212` na caixa. E o 55 sai quando o número vem com o código do país
 * (colado de outro lugar, "+55 11 99999-9999" dá 13 dígitos): sem tirar, a
 * máscara transformava o DDI em DDD e escrevia "(55) 11999-9999". Só corta acima
 * de 11 dígitos, senão o DDD 55 (Santa Maria) seria comido ao digitar.
 */
export function digitosDeTelefoneBR(bruto: string): string {
  let d = soDigitos(bruto)
  if (d.startsWith("55") && d.length > 11) d = d.slice(2)
  return d.slice(0, 11)
}

/** `11999999999` → `(11) 99999-9999`. */
export function telefoneBREscrito(valor: string): string {
  return aplicarMascaraTelefone(digitosDeTelefoneBR(valor), MASCARA_BR)
}

/**
 * O celular que o Pix e o cartão do split aceitam: DDD e número do Brasil (10 ou 11
 * dígitos), com ou sem o 55 na frente. Devolve os dígitos sem o país, ou `null`.
 *
 * É a mesma regra da API (`celularDoPagamento`, em `api/src/modules/split/regras-do-split.ts`):
 * o Pagar.me recusa o Pix e o cartão sem o telefone de quem paga (o Pix medido no
 * sandbox em 21/10/2026; o cartão, no teste manual do staging em 24/10/2026), e a API
 * monta esse telefone com o país fixo no Brasil. Um número de outro país (`+351...`)
 * não serve, e a tela precisa dizer isso antes de pedir o Pix ou mandar o cartão. O
 * nome ficou do tempo em que só o Pix pedia.
 */
export function celularDoPix(valor: string | null | undefined): string | null {
  const bruto = (valor ?? "").trim()
  const d = soDigitos(bruto)
  if (bruto.startsWith("+") && !d.startsWith("55")) return null
  const semPais = (d.length === 12 || d.length === 13) && d.startsWith("55") ? d.slice(2) : d
  return /^\d{10,11}$/.test(semPais) ? semPais : null
}
